import React, { useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { Button } from 'travelyo-react-shared-components';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { scrollToResults, isMobile } from '../../utilities/helpers';
import translate from '../../utilities/translator';
import { pushEvent } from '../../utilities/analytics';
import {
  FILTER_RESET_X_EVENT,
  FILTER_RESET_BUTTON_EVENT,
  FILTER_OPEN,
  FILTER_CLOSE,
} from '../../constants/analyticEvents';
import MainFilterSmall from '../../components/mainFilter/MainFilterSmall';
import FadeTransition from '../../components/animations/FadeTransition';
import { setShouldScroll } from '../../appSlice';
import { useDispatch } from 'react-redux';
import useStickFilterPosition from '../../hooks/useStickFilterPosition';
import MainFilterTooltip from './tooltip/MainFilterTooltip';
import useSearchPerformed from '../../hooks/useSearchPerformed';

const FilterItem = forwardRef((props, ref) => {
  const {
    label,
    placeholder,
    value,
    children,
    onSave,
    onClose,
    onClear,
    icon = '',
    showClearButton,
    additionalClass,
    saveLabel,
    clearLabel,
    id,
    resetOnClickOutside,
    eventName,
    clean = false,
    isOpen,
    setIsOpen,
    showTooltip,
    tooltipMessage,
    onTooltipClick,
    isLoginOpen = false,
  } = props;

  const filterRef = useRef();
  const popupRef = useRef();

  const dispatch = useDispatch();

  const [setAppTimeout, clearAppTimeout] = useSearchPerformed();

  useOnClickOutside(filterRef, (event) => {
    const path = event.path || (event.composedPath && event.composedPath());
    const isClickedToFilterItem = !!Array.prototype.find.call(
      path,
      (element) => {
        return element.classList?.contains('main-filter__item');
      }
    );

    const isOpenFilter = filterRef.current.classList.contains(
      'main-filter__item--open'
    );

    if (isOpen && !isMobile()) {
      setIsOpen(false);
      pushEvent(FILTER_CLOSE, { filterName: eventName, value });
      if (eventName !== 'budget') {
        setAppTimeout(7000, true);
      }

      if (isClickedToFilterItem && isOpenFilter && !clean) {
        onSave();
      } else if (resetOnClickOutside) {
        resetOnClickOutside();
      }
    }
  });

  useBodyScrollLock({ condition: isOpen, mobileOnly: true });

  useImperativeHandle(ref, () => ({
    onClose() {
      setIsOpen(false);
      pushEvent(FILTER_CLOSE, { filterName: eventName, value });
      if (eventName !== 'budget') {
        setAppTimeout(7000, true);
      }
    },
  }));

  const filterPositionClassName = useStickFilterPosition(
    popupRef,
    filterRef,
    isOpen
  );

  const onClearClick = useCallback((event) => {
    scrollToResults();
    dispatch(setShouldScroll(true));
    event.stopPropagation();
    pushEvent(FILTER_RESET_X_EVENT, { filterName: eventName });
    onClear();
    onClose && onClose();
  }, [onClear, onClose, eventName, dispatch]);

  const onCloseClick = useCallback(() => {
    if (resetOnClickOutside) {
      resetOnClickOutside();
    }
    onClose && onClose();
    setIsOpen(false);
  }, [onClose, resetOnClickOutside, setIsOpen]);

  const handleOpenFilter = useCallback(() => {
    if (document.querySelector('.main-filter__wrap--sticky')) {
      setIsOpen(true);
    } else {
      if (isMobile()) {
        setIsOpen(true);
      } else {
        setIsOpen(true);
        setTimeout(() => scrollToResults(), 50); //scrollToResults().then(() => setIsOpen(true));
      }
    }
    pushEvent(FILTER_OPEN, { filterName: eventName, value });
    if (eventName !== 'budget') {
      clearAppTimeout();
    }
    window.history.pushState({ type: 'something' }, null, window.location.href);
  }, [setIsOpen, clearAppTimeout, eventName, value]);

  const modalContent = (
    <FadeTransition in={isOpen} unmountOnExit>
      <div
        className={classNames(`filter-popup ${filterPositionClassName}`, {
          [`${additionalClass}`]: additionalClass,
          'filter-popup--login-opened': isLoginOpen,
        })}
        ref={popupRef}
      >
        <div className="filter-popup__header">{label}</div>
        <div className="filter-popup__body">{children}</div>
        <div className="filter-popup__footer">
          <div className="w-full flex gap-3 justify-end">
            <Button
              variant="secondary"
              rounded
              label={clearLabel || translate('holidayFinder.buttons.Clear')}
              size="large"
              onClick={(event) => {
                onClear(event);
                setIsOpen(false);
                pushEvent(FILTER_RESET_BUTTON_EVENT, { filterName: eventName });
              }}
            />
            <Button
              variant="primary"
              rounded
              label={saveLabel || translate('holidayFinder.buttons.Save')}
              size="large"
              disabled={clean}
              onClick={(e) => {
                onSave(e);
                scrollToResults();
              }}
              className="w-full max-w-65"
            />
          </div>

          <button
            className="icon-close filter-popup__close"
            onClick={() => {
              onCloseClick();
              setIsOpen(false);
            }}
          />
        </div>
      </div>
    </FadeTransition>
  );

  const mainFilterSmallComponent = (
    <MainFilterSmall
      id={id}
      placeholder={placeholder}
      label={label}
      value={value}
      changed={showClearButton}
      onClearClick={onClearClick}
      openFilter={handleOpenFilter}
      icon={icon}
      clean={clean}
      isOpen={isOpen}
      showClearButton={showClearButton}
    />
  );

  return (
    <div
      className={classNames('main-filter__item', {
        'main-filter__item--open': isOpen,
      })}
      ref={filterRef}
    >
      {tooltipMessage ? (
        <MainFilterTooltip
          show={showTooltip}
          label={tooltipMessage}
          offset={[0, -5]}
          onTooltipClick={onTooltipClick}
        >
          {mainFilterSmallComponent}
        </MainFilterTooltip>
      ) : (
        mainFilterSmallComponent
      )}
      {window.innerWidth <= 768
        ? createPortal(
            modalContent,
            document.getElementById('main-filter-portal')
          )
        : modalContent}
    </div>
  );
});

FilterItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any.isRequired,
  children: PropTypes.node.isRequired,
  onSave: PropTypes.func,
  classPrefix: PropTypes.string,
};

export default FilterItem;
